import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from '@phosphor-icons/react';
import type {
  TaskTypeView,
  TaskTypeViewSupplierLineMatchColumn,
  TaskTypeViewSupplierLineMatchPlacement,
} from '@erp/shared-types';
import { useGetSchemaDefinition } from 'src/api/rpc/hooks/core/useSchemaDefinitions';
import { SchemaDefinitionPicker } from 'src/components/schema-definitions/SchemaDefinitionPicker';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  addAllExtractedSupplierLineColumns,
  addSupplierLineColumn,
  MAX_VIEW_COLUMNS,
  moveSupplierLineColumn,
  removeSupplierLineColumn,
  setSupplierLineColumnLabel,
  setSupplierLineColumnPath,
  setSupplierLineColumnSchema,
  supplierLineColumnPathFromText,
  supplierLineColumnPathText,
  supplierLineColumnSuggestions,
  viewSchemaFieldsFromWire,
  type ViewFieldOption,
} from './taskTypeViewModel';
import { ViewLabelInput, ViewPathSelect } from './TaskTypeViewPathControls';

const NO_COLUMN_SCHEMA = '__none__';
const CUSTOM_PATH = '__custom__';

/**
 * Extracted columns on a supplier-line remap. The task field has no schema
 * children, so the author points at the extraction schema the match node
 * used and picks its row fields as columns; a typed path is the fallback.
 */
export function TaskTypeViewSupplierLineColumns({
  view,
  regionId,
  placement,
  disabled,
  onCommit,
}: {
  view: TaskTypeView;
  regionId: string;
  placement: TaskTypeViewSupplierLineMatchPlacement;
  disabled?: boolean;
  onCommit: (next: TaskTypeView) => void;
}) {
  const gt = useGT();
  const columns = placement.config.columns;
  const atCap = columns.length >= MAX_VIEW_COLUMNS;
  const schemaId = placement.config.columnSchemaDefinitionId;
  const suggestions = useColumnSuggestions(schemaId);
  const extractedLeft = suggestions.filter(
    (option) =>
      option.path[0] === 'extracted' &&
      !columns.some(
        (column) => column.path.join('.') === option.path.join('.'),
      ),
  ).length;
  function commit(next: TaskTypeView | null) {
    if (next && next !== view) onCommit(next);
  }

  return (
    <div className="flex flex-col gap-2 border-l pl-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          <T context="task page builder, label for the extraction schema that supplies supplier line columns">
            Columns from
          </T>
        </span>
        <SchemaDefinitionPicker
          value={schemaId ?? NO_COLUMN_SCHEMA}
          disabled={disabled}
          onChange={(value) =>
            commit(
              setSupplierLineColumnSchema(
                view,
                regionId,
                placement.id,
                value === NO_COLUMN_SCHEMA ? null : value,
              ),
            )
          }
          noneOption={{
            id: NO_COLUMN_SCHEMA,
            label: gt('No schema — type each column path', {
              context:
                'task page builder, supplier line columns without an extraction schema',
            }),
            display: gt('Typed paths', {
              context:
                'task page builder, short display when supplier line columns have no extraction schema',
            }),
          }}
        />
        <span className="text-xs text-muted-foreground">
          <T context="task page builder, hint next to the supplier line column schema picker">
            Pick the extraction schema the Match Supplier Document Lines step is
            pinned to. Its row fields become columns.
          </T>
        </span>
      </div>
      {columns.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          <T context="task page builder, supplier lines with no extracted columns yet">
            No extracted columns yet. Each column is a value on one match row,
            such as its quantity or description.
          </T>
        </p>
      ) : (
        columns.map((column, index) => (
          <SupplierLineColumnRow
            key={column.id}
            column={column}
            suggestions={suggestions}
            canMoveUp={index > 0}
            canMoveDown={index < columns.length - 1}
            disabled={disabled}
            onPath={(path) =>
              commit(
                setSupplierLineColumnPath(
                  view,
                  regionId,
                  placement.id,
                  column.id,
                  path,
                ),
              )
            }
            onLabel={(label) =>
              commit(
                setSupplierLineColumnLabel(
                  view,
                  regionId,
                  placement.id,
                  column.id,
                  label,
                ),
              )
            }
            onMove={(direction) =>
              commit(
                moveSupplierLineColumn(
                  view,
                  regionId,
                  placement.id,
                  column.id,
                  direction,
                ),
              )
            }
            onRemove={() =>
              commit(
                removeSupplierLineColumn(
                  view,
                  regionId,
                  placement.id,
                  column.id,
                ),
              )
            }
          />
        ))
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={Boolean(disabled) || atCap}
          onClick={() =>
            commit(
              addSupplierLineColumn(view, regionId, placement.id, suggestions),
            )
          }
        >
          <T context="task page builder, add an extracted column to supplier lines">
            Add Column
          </T>
        </Button>
        {suggestions.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={Boolean(disabled) || atCap || extractedLeft === 0}
            onClick={() =>
              commit(
                addAllExtractedSupplierLineColumns(
                  view,
                  regionId,
                  placement.id,
                  suggestions,
                ),
              )
            }
          >
            <T context="task page builder, add every extraction schema field as a supplier line column">
              Add All Extracted
            </T>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Row fields of the chosen extraction schema, as column options. */
function useColumnSuggestions(schemaId: string | null): ViewFieldOption[] {
  const query = useGetSchemaDefinition(schemaId ?? '');
  if (!schemaId || !query.data) return [];
  return supplierLineColumnSuggestions(
    viewSchemaFieldsFromWire(query.data.fields),
  );
}

function SupplierLineColumnRow({
  column,
  suggestions,
  canMoveUp,
  canMoveDown,
  disabled,
  onPath,
  onLabel,
  onMove,
  onRemove,
}: {
  column: TaskTypeViewSupplierLineMatchColumn;
  suggestions: readonly ViewFieldOption[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  disabled?: boolean;
  onPath: (path: string[]) => void;
  onLabel: (label: string) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-16 text-xs font-medium text-muted-foreground">
        <T context="task page builder item kind, an extracted column on a supplier line">
          Column
        </T>
      </span>
      <SupplierLineColumnPathControl
        path={column.path}
        suggestions={suggestions}
        disabled={disabled}
        onChange={onPath}
      />
      <ViewLabelInput
        label={column.label}
        disabled={disabled}
        onChange={onLabel}
      />
      <div className="ml-auto flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled || !canMoveUp}
          aria-label={gt('Move column up', {
            context: 'task page builder, move a supplier line column up',
          })}
          onClick={() => onMove(-1)}
        >
          <ArrowUpIcon />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled || !canMoveDown}
          aria-label={gt('Move column down', {
            context: 'task page builder, move a supplier line column down',
          })}
          onClick={() => onMove(1)}
        >
          <ArrowDownIcon />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          aria-label={gt('Remove column', {
            context: 'task page builder, remove a supplier line column',
          })}
          onClick={onRemove}
        >
          <TrashIcon />
        </Button>
      </div>
    </div>
  );
}

/**
 * With suggestions, a select over the schema's row fields and the match-row
 * keys, plus a Custom entry that reveals the typed path. Without them, the
 * typed path alone.
 */
function SupplierLineColumnPathControl({
  path,
  suggestions,
  disabled,
  onChange,
}: {
  path: readonly string[];
  suggestions: readonly ViewFieldOption[];
  disabled?: boolean;
  onChange: (path: string[]) => void;
}) {
  const gt = useGT();
  const [custom, setCustom] = useState(false);
  const known = suggestions.some(
    (option) => option.path.join('.') === path.join('.'),
  );
  if (suggestions.length === 0 || (custom && !known)) {
    return (
      <SupplierLineColumnPathInput
        path={path}
        disabled={disabled}
        onChange={onChange}
      />
    );
  }
  return (
    <ViewPathSelect
      options={suggestions}
      path={path}
      disabled={disabled}
      onChange={(next) => {
        setCustom(false);
        onChange(next);
      }}
      missingLabel={(label) =>
        gt('Custom: {path}', {
          context:
            'task page builder, supplier line column option for a typed path not on the schema',
          path: label,
        })
      }
      trailing={{
        value: CUSTOM_PATH,
        label: (
          <T context="task page builder, supplier line column option to type a path by hand">
            Custom path…
          </T>
        ),
      }}
      onTrailing={() => setCustom(true)}
    />
  );
}

/**
 * Dotted path, committed on blur. Text that does not parse as a path keeps
 * the saved path so an in-progress edit never writes an empty segment.
 */
function SupplierLineColumnPathInput({
  path,
  disabled,
  onChange,
}: {
  path: readonly string[];
  disabled?: boolean;
  onChange: (path: string[]) => void;
}) {
  const gt = useGT();
  const saved = supplierLineColumnPathText(path);
  const [draft, setDraft] = useState<string | null>(null);
  if (draft !== null && draft === saved) setDraft(null);

  function commitDraft(text: string) {
    const parsed = supplierLineColumnPathFromText(text);
    setDraft(null);
    if (parsed) onChange(parsed);
  }

  return (
    <Input
      value={draft ?? saved}
      disabled={disabled}
      className="w-56 font-mono text-xs"
      placeholder={gt('extracted.quantity', {
        context:
          'task page builder, example dotted path for a supplier line column; keep as a code-like path',
      })}
      aria-label={gt('Column path', {
        context:
          'task page builder, dotted path of an extracted column relative to one supplier line',
      })}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={(event) => commitDraft(event.currentTarget.value)}
    />
  );
}
